import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Divider,
  Box,
  Select,
  Menu,
  MenuItem,
  Typography,
  InputLabel,
  Stack,
  FormControl
} from "@mui/material";
import { ProgressButton } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useResizeDetector } from "react-resize-detector";

import { useAppSelector } from "~/app/store";

import WorkstationSelectorModal from "~/features/andon/WorkstationSelectorModal";
import { selectLayoutCode } from "~/features/andon/andon.selectors";
import {
  setAndonViewOption,
  setBoardViewIds,
  setBoardLayout,
  setBoardDimensions,
  selectAndonBoardViewsIds,
  selectAndonBoardLayout,
  selectAndonBoardDimensions,
  selectCurrentAndonBoardView,
  selectEditModeEnabled,
  selectSelectedAndonLayoutName,
  setSelectedAndonLayoutName
} from "~/features/andon/andon.slice";
import { chooseVariantFromCell } from "~/features/andon/andon.utils";

import { useClientConfig } from "~/hooks/useClientConfig";

import {
  createNumberRangeArr,
  findClosestEmptyPositions,
  getEmptyPositions,
  convertPixelCoordinatesToGridCoordinates
} from "~/lib/helpers";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import AndonBoardElement from "./AndonBoardElement";
import DragAndDropGridLayout from "./DragAndDropGridLayout";

export default function AndonBoard() {
  const {
    width: boardWidth,
    height: boardHeight,
    ref: boardRef
  } = useResizeDetector();

  const { data: workstations } = useGetWorkstationsQuery();

  const { an_boardLayouts } = useClientConfig();

  const [selectedGridIndex, setSelectedGridIndex] = useState<string | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorPosition, setAnchorPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [workstationSelectorOpen, setWorkstationSelectorOpen] = useState(false);
  const boardViewIds = useAppSelector(selectAndonBoardViewsIds);
  const boardLayout = useAppSelector(selectAndonBoardLayout);
  const boardDimensions = useAppSelector(selectAndonBoardDimensions);
  const selectedLayoutName = useAppSelector(selectSelectedAndonLayoutName);
  const layoutCode = useAppSelector(selectLayoutCode);

  const open = Boolean(anchorEl || anchorPosition);

  const gridHeightPercentage = 100 / boardDimensions.rows;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const andonViewOption = useAppSelector(selectCurrentAndonBoardView);
  const isFullScreen = andonViewOption === "board fullscreen";
  const editModeEnabled = useAppSelector(selectEditModeEnabled);

  const updateLayoutAndDimensions = (
    dimensionType: string,
    newValue: number
  ) => {
    const newAndonLayout = boardLayout
      .filter((item) => {
        if (dimensionType === "columns") {
          return item.x + item.w <= newValue;
        } else {
          return item.y + item.h <= newValue;
        }
      })
      .map((item) => ({
        ...item,
        // Recalculate maxH for the change in rows
        ...(dimensionType === "rows" && { maxH: newValue - item.y })
      }));

    dispatch(setBoardLayout(newAndonLayout));
    dispatch(
      setBoardDimensions({
        columns: dimensionType === "rows" ? boardDimensions.columns : newValue,
        rows: dimensionType === "columns" ? boardDimensions.rows : newValue
      })
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>, index: string) => {
    if (!editModeEnabled) return;

    setAnchorEl(event.currentTarget);
    setSelectedGridIndex(index);
  };

  const handleGridClick = (event: MouseEvent) => {
    if (!editModeEnabled) return;

    const emptyCoordinates = getEmptyPositions(
      boardLayout,
      boardDimensions.columns,
      boardDimensions.rows
    );

    if (emptyCoordinates.length) {
      setAnchorPosition({
        left: event.clientX,
        top: event.clientY
      });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedGridIndex(null);
    setAnchorPosition(null);
  };

  const removeWorkstation = (workstationId: string) => {
    const updatedLayout = boardLayout.filter(
      (item) => item.i !== workstationId
    );
    dispatch(setBoardLayout(updatedLayout));
    setAnchorEl(null);
    setSelectedGridIndex(null);
  };

  const selectWorkstation = (workstationId: string | null) => {
    // the x option removes the workstation
    if (!workstationId && selectedGridIndex != null) {
      removeWorkstation(selectedGridIndex);

      // if you select an existing element/workstation, update with the newly selected workstation
    } else if (workstationId && selectedGridIndex != null) {
      const updatedLayout = boardLayout.map((item) => {
        if (item.i === selectedGridIndex) {
          return { ...item, i: workstationId };
        }
        return item;
      });

      dispatch(setBoardLayout(updatedLayout));

      // from grid clicks (not workstation clicks)
    } else {
      const grid = document.querySelector(`.andon-board`);

      if (grid && anchorPosition) {
        const andonBoardDiv = grid.getBoundingClientRect();

        const clickedGridPosition = convertPixelCoordinatesToGridCoordinates(
          // use the saved anchor position minus offsets when sending pixel location
          anchorPosition.left - andonBoardDiv.left,
          anchorPosition.top - andonBoardDiv.top,
          boardWidth || 0,
          boardHeight ? boardHeight * (gridHeightPercentage / 100) : 0,
          boardDimensions.columns
        );

        const emptyCoordinates = getEmptyPositions(
          boardLayout,
          boardDimensions.columns,
          boardDimensions.rows
        );

        const closestPositions = findClosestEmptyPositions(
          clickedGridPosition,
          emptyCoordinates
        );

        if (closestPositions && workstationId) {
          const newItem = {
            i: workstationId,
            x: closestPositions.x,
            y: closestPositions.y,
            w: 1,
            h: 1,
            maxH: boardDimensions.rows - closestPositions.y // Setting maxH based on available rows
          };

          const updatedLayout = [...boardLayout, newItem];

          dispatch(setBoardLayout(updatedLayout));
        }
      }
    }

    handleClose();
    setSelectedGridIndex(null);
  };

  const workstationsForDropdown = workstations
    ?.filter(
      (ws) =>
        boardViewIds.includes(ws.id) &&
        !boardLayout.find((layout) => layout.i === ws.id)
    )
    .slice()
    .sort(
      (a, b) =>
        a.autostoreGridName.localeCompare(b.autostoreGridName) ||
        a.deviceId.localeCompare(b.deviceId)
    );

  return (
    <Box sx={{ height: "100%", px: 4 }} ref={boardRef}>
      {/* top row, buttons etc */}
      {!isFullScreen && (
        <Stack direction="row" gap={1} alignItems="center">
          {/* Layout selector */}
          {!!an_boardLayouts.length && (
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, ml: 2 }}
            >
              <Select
                value={selectedLayoutName ?? ""}
                displayEmpty
                size="small"
                sx={{ height: "40px" }}
                /* renderValue lets you show a true placeholder when no value */
                renderValue={(val) =>
                  val ? (
                    val
                  ) : (
                    <Typography color="text.disabled">
                      {t("select a layout...")}
                    </Typography>
                  )
                }
                onChange={(e) => {
                  const name = e.target.value;
                  const layout = an_boardLayouts.find((l) => l.name === name);
                  if (layout) {
                    dispatch(setBoardLayout(layout.layout));
                    dispatch(setBoardDimensions(layout.dimensions));
                    dispatch(setSelectedAndonLayoutName(layout.name));
                    const idsInLayout = layout.layout.map((item) => item.i);
                    dispatch(setBoardViewIds(idsInLayout));
                  }
                }}
              >
                {an_boardLayouts.map((layout) => (
                  <MenuItem key={layout.name} value={layout.name}>
                    {layout.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* fullscreen button */}
          <ProgressButton
            emphasis="high"
            responsive
            variant="contained"
            color="secondary"
            sx={{ marginLeft: "10px" }}
            disabled={!boardLayout.length}
            onClick={() => {
              dispatch(setAndonViewOption("board fullscreen"));
            }}
          >
            {t("full screen")}
          </ProgressButton>

          {editModeEnabled && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <ProgressButton
                emphasis="high"
                responsive
                variant="contained"
                color="secondary"
                onClick={() => {
                  setWorkstationSelectorOpen(true);
                }}
              >
                {t("select workstations")}
              </ProgressButton>
              <ProgressButton
                emphasis="high"
                responsive
                variant="contained"
                color="secondary"
                sx={{ marginLeft: "10px" }}
                onClick={() => {
                  dispatch(setBoardLayout([]));
                  dispatch(setSelectedAndonLayoutName(null));
                }}
              >
                {t("clear")}
              </ProgressButton>
              {/* columns */}
              <InputLabel
                id="andon-dimensions-columns-label"
                htmlFor="andon-dimensions-columns"
              >
                <Typography variant="button">{t("columns")}</Typography>
              </InputLabel>
              <Select
                id="andon-dimensions-columns"
                value={boardDimensions.columns}
                sx={{ height: "40px", width: "75px" }}
                labelId="andon-dimensions-columns-label"
              >
                {createNumberRangeArr(1, 10).map((numb) => (
                  <MenuItem
                    key={`${numb}columns`}
                    value={numb}
                    onClick={() => updateLayoutAndDimensions("columns", numb)}
                  >
                    {numb}
                  </MenuItem>
                ))}
              </Select>
              {/* rows */}
              <InputLabel
                id="andon-dimensions-rows-label"
                htmlFor="andon-dimensions-rows"
              >
                <Typography variant="button">{t("rows")}</Typography>
              </InputLabel>
              <Select
                id="andon-dimensions-rows"
                value={boardDimensions.rows}
                labelId="andon-dimensions-rows-label"
                sx={{ height: "40px", width: "75px" }}
              >
                {createNumberRangeArr(1, 10).map((numb) => (
                  <MenuItem
                    key={`${numb}rows`}
                    value={numb}
                    onClick={() => updateLayoutAndDimensions("rows", numb)}
                  >
                    {numb}
                  </MenuItem>
                ))}
              </Select>
              <ProgressButton
                emphasis="high"
                responsive
                variant="contained"
                color="secondary"
                sx={{ marginLeft: "10px" }}
                onClick={() => navigator.clipboard.writeText(layoutCode)}
                startIcon={<ContentCopyIcon />}
              >
                {t("copy layout code")}
              </ProgressButton>
            </>
          )}
        </Stack>
      )}

      <Box
        sx={{
          width: "100%",
          height: isFullScreen ? "100%" : "70vh"
        }}
        role="region"
        aria-label="Andon board"
      >
        {boardWidth && boardHeight && (
          <DragAndDropGridLayout
            sizeLocked={!editModeEnabled}
            isDraggable={editModeEnabled}
            className="andon-board"
            layout={boardLayout}
            cols={boardDimensions.columns}
            rows={boardDimensions.rows}
            width={boardWidth}
            rowHeight={
              (isFullScreen ? boardHeight : boardHeight * 0.9) *
                (gridHeightPercentage / 100) -
              1
            }
            onLayoutChange={(layout) => dispatch(setBoardLayout(layout))}
            preventCollision
            onExistingElementClick={handleClick}
            onGridClick={handleGridClick}
            renderElementWithIndex={(workstationId) => {
              const ws = workstations?.find((w) => w.id === workstationId);
              if (!ws) return null;

              const matchingLayout = boardLayout.find(
                (lo) => lo.i === workstationId
              );
              const widthSpan = matchingLayout?.w || 1;
              const heightSpan = matchingLayout?.h || 1;

              const widthPx = boardWidth
                ? boardWidth * (widthSpan / boardDimensions.columns)
                : 0;
              const heightPx = boardHeight
                ? boardHeight * (heightSpan / boardDimensions.rows)
                : 0;

              const variant = chooseVariantFromCell(widthPx, heightPx);

              const isCompact = variant === "body2" || variant === "body1";
              return (
                <AndonBoardElement
                  workstationId={workstationId}
                  variant={variant}
                  isCompact={isCompact}
                />
              );
            }}
          />
        )}

        <Menu
          id="workstation-menu"
          anchorEl={anchorEl}
          anchorPosition={anchorPosition || undefined}
          anchorReference={anchorEl ? undefined : "anchorPosition"}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "select-workstation"
          }}
        >
          {workstationsForDropdown?.map((workstation) => (
            <MenuItem
              key={`${workstation.id}-choice`}
              onClick={() => selectWorkstation(workstation.id)}
            >
              <Typography>{workstation.deviceId}</Typography>
            </MenuItem>
          ))}
          {selectedGridIndex && (
            <MenuItem key="null" onClick={() => selectWorkstation(null)}>
              <Typography>X</Typography>
            </MenuItem>
          )}
        </Menu>
      </Box>
      {/* modals */}
      <WorkstationSelectorModal
        isOpen={workstationSelectorOpen}
        selectedWorkstationIds={boardViewIds}
        workstationSelectorCb={(newIds) => {
          dispatch(setBoardViewIds(newIds));
        }}
        setModalOpen={(isOpen) => setWorkstationSelectorOpen(isOpen)}
        footer={
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
          >
            <Box sx={{ width: "150px" }}>
              <ProgressButton
                emphasis="high"
                responsive
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => {
                  const gridItemWidth = 1;
                  const gridItemHeight = 1;

                  const numberOfElements =
                    boardDimensions.rows * boardDimensions.columns;

                  const newLayout = boardViewIds
                    .slice(0, numberOfElements)
                    .map((workstationId, index) => {
                      // Calculate the position of each workstation based on its index
                      const positionX =
                        (index % boardDimensions.columns) * gridItemWidth;
                      const positionY =
                        Math.floor(index / boardDimensions.columns) *
                        gridItemHeight;

                      return {
                        i: workstationId,
                        x: positionX,
                        y: positionY,
                        w: gridItemWidth,
                        h: gridItemHeight,
                        maxH: boardDimensions.rows - positionY
                      };
                    });
                  dispatch(setBoardLayout(newLayout));
                  setWorkstationSelectorOpen(false);
                }}
              >
                {t("autofill")}
              </ProgressButton>
            </Box>
          </Box>
        }
      />
    </Box>
  );
}
